import React, { useState, useEffect } from 'react';
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  Power,
  Loader2,
  Code,
  RefreshCw,
  AlertCircle,
  Eye,
  EyeOff,
  ExternalLink,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { apiConfigAPI, usersAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

const ApiConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [curlDetails, setCurlDetails] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const { confirm, dialogProps } = useConfirm();

  const [copiedField, setCopiedField] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});

  const [newConfig, setNewConfig] = useState({
    name: '',
    description: '',
    auto_assign_to_user_id: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configsRes, usersRes] = await Promise.all([
        apiConfigAPI.list(),
        usersAPI.list()
      ]);
      setConfigs(configsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await apiConfigAPI.create({
        ...newConfig,
        auto_assign_to_user_id: newConfig.auto_assign_to_user_id === 'auto-assign' ? null : newConfig.auto_assign_to_user_id || null
      });
      await fetchData();
      setIsCreateOpen(false);
      setNewConfig({ name: '', description: '', auto_assign_to_user_id: '', is_active: true });
    } catch (error) {
      console.error('Failed to create config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to create API config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleToggle = async (configId) => {
    try {
      await apiConfigAPI.toggle(configId);
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDelete = async () => {
    if (!configToDelete) return;

    const ok = await confirm({
      title: 'Delete API configuration?',
      description: `This will delete "${configToDelete.name}".`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true
    });
    if (!ok) return;

    try {
      await apiConfigAPI.delete(configToDelete.id);
      await fetchData();
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
      toast({ title: 'Configuration deleted' });
    } catch (error) {
      console.error('Failed to delete config:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete config',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleViewDetails = async (config) => {
    try {
      const response = await apiConfigAPI.getCurlExample(config.id);
      setCurlDetails(response.data);
      setSelectedConfig(config);
      setIsDetailsOpen(true);
    } catch (error) {
      console.error('Failed to get curl example:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to load details',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Auto-assign';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading API configurations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">API Configuration</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Create API keys for external lead integration
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button className="bg-slate-800 hover:bg-slate-900" onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-800">External Lead Creation API</p>
              <p className="text-sm text-blue-700">
                Create API configurations to allow external systems (webhooks, forms, etc.) to automatically create leads in your CRM.
                Each configuration gets a unique API key and endpoint URL with field mapping documentation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Configs Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Name</TableHead>
                <TableHead className="font-semibold">API Key</TableHead>
                <TableHead className="font-semibold">Assign To</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Leads Created</TableHead>
                <TableHead className="font-semibold">Last Used</TableHead>
                <TableHead className="w-[150px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Key className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No API configurations yet</p>
                    <p className="text-sm">Create your first API key to start receiving leads from external sources</p>
                  </TableCell>
                </TableRow>
              ) : configs.map((config) => (
                <TableRow key={config.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-100">{config.name}</p>
                      {config.description && (
                        <p className="text-xs text-slate-500">{config.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono">
                        {showApiKey[config.id] 
                          ? config.api_key 
                          : config.api_key?.substring(0, 8) + '...' + config.api_key?.substring(config.api_key.length - 4)}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setShowApiKey({ ...showApiKey, [config.id]: !showApiKey[config.id] })}
                      >
                        {showApiKey[config.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(config.api_key, `key-${config.id}`)}
                      >
                        {copiedField === `key-${config.id}` ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {getUserName(config.auto_assign_to_user_id)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn(
                      config.is_active 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-slate-100 text-slate-600'
                    )}>
                      {config.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-slate-800 dark:text-slate-100">
                      {config.total_leads_created || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {config.last_used_at 
                        ? new Date(config.last_used_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Never'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => handleViewDetails(config)}
                      >
                        <Code className="w-4 h-4 mr-1" />
                        Details
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          'h-8 w-8 p-0',
                          config.is_active ? 'text-emerald-600' : 'text-slate-400'
                        )}
                        onClick={() => handleToggle(config.id)}
                        title={config.is_active ? 'Disable' : 'Enable'}
                      >
                        <Power className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        onClick={() => { setConfigToDelete(config); setDeleteConfirmOpen(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Create API Configuration
            </DialogTitle>
            <DialogDescription>
              Create a new API key for external lead integration
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newConfig.name}
                onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                placeholder="e.g., Website Contact Form"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newConfig.description}
                onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div className="space-y-2">
              <Label>Auto-assign Leads To</Label>
              <Select
                value={newConfig.auto_assign_to_user_id}
                onValueChange={(v) => setNewConfig({ ...newConfig, auto_assign_to_user_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Auto-assign (round-robin)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto-assign">Auto-assign (round-robin)</SelectItem>
                  {users.filter(u => u.role === 'EMPLOYEE' || u.role === 'MANAGER').map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Leave empty to auto-assign to user with fewest new leads
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreate}
              disabled={!newConfig.name.trim()}
              className="bg-slate-800 hover:bg-slate-900"
            >
              Create API Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Modal with curl example */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5" />
              API Details - {selectedConfig?.name}
            </DialogTitle>
          </DialogHeader>
          
          {curlDetails && (
            <Tabs defaultValue="curl" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="curl">cURL Example</TabsTrigger>
                <TabsTrigger value="fields">Field Mapping</TabsTrigger>
                <TabsTrigger value="credentials">Credentials</TabsTrigger>
              </TabsList>
              
              <TabsContent value="curl" className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Endpoint URL</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(curlDetails.endpoint_url, 'endpoint')}
                    >
                      {copiedField === 'endpoint' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </Label>
                  <code className="block p-3 bg-slate-100 dark:bg-slate-800 rounded text-sm break-all">
                    {curlDetails.endpoint_url}
                  </code>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center justify-between">
                    <span>Full cURL Command</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(curlDetails.curl_example, 'curl')}
                    >
                      {copiedField === 'curl' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </Label>
                  <pre className="p-4 bg-slate-900 text-slate-100 rounded text-xs overflow-x-auto">
                    {curlDetails.curl_example}
                  </pre>
                </div>
              </TabsContent>
              
              <TabsContent value="fields" className="space-y-4">
                <div className="grid gap-2">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-red-500" />
                    Required Fields
                  </h4>
                  <div className="grid gap-2">
                    {curlDetails.required_fields?.map(field => (
                      <div key={field} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-200">
                        <code className="text-sm font-mono">{field}</code>
                        <span className="text-xs text-red-600">
                          {curlDetails.field_mapping?.[field]?.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <h4 className="font-medium text-slate-800 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    Optional Fields
                  </h4>
                  <div className="grid gap-2">
                    {curlDetails.optional_fields?.map(field => (
                      <div key={field} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200">
                        <code className="text-sm font-mono">{field}</code>
                        <span className="text-xs text-slate-500">
                          {curlDetails.field_mapping?.[field]?.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> If <code className="bg-amber-100 px-1 rounded">poc_name</code> is empty, it will automatically use <code className="bg-amber-100 px-1 rounded">director_name</code>. 
                    If <code className="bg-amber-100 px-1 rounded">user_id</code> is not provided, the lead will be auto-assigned based on the configuration.
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="credentials" className="space-y-4">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono break-all">
                      {curlDetails.api_key}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(curlDetails.api_key, 'api-key-detail')}
                    >
                      {copiedField === 'api-key-detail' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Headers</Label>
                  <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded text-sm">
                    <div className="flex justify-between">
                      <code>Content-Type:</code>
                      <code>application/json</code>
                    </div>
                    <div className="flex justify-between mt-1">
                      <code>X-API-Key:</code>
                      <code>[Your API Key]</code>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>HTTP Method</Label>
                  <Badge className="bg-blue-100 text-blue-700">POST</Badge>
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </div>
  );
};

export default ApiConfigPage;
